import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Minimal GDPR / ePrivacy-style cookie banner.
 *
 * We only distinguish two buckets: essential (always on) and non-essential
 * (analytics + error monitoring). Users can accept, decline, or ignore —
 * ignoring == declining for non-essentials until an explicit choice is made.
 *
 * Choice is stored in localStorage under `rh_cookie_consent` and dispatched
 * as a window event `roomhora:consent` so downstream scripts can react.
 */

const STORAGE_KEY = 'rh_cookie_consent';
const STORAGE_VERSION = 1; // bump when policy materially changes to re-prompt

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== STORAGE_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(choice) {
  try {
    const payload = {
      version: STORAGE_VERSION,
      choice, // 'accepted' | 'declined'
      ts: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    window.dispatchEvent(new CustomEvent('roomhora:consent', { detail: payload }));
  } catch {
    // storage unavailable — silently ignore, banner will just show again
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Respect Do Not Track — auto-decline and skip the banner.
    const dnt =
      typeof navigator !== 'undefined' &&
      (navigator.doNotTrack === '1' ||
        window.doNotTrack === '1' ||
        navigator.msDoNotTrack === '1');

    const stored = readStored();
    if (stored) return; // already chose
    if (dnt) {
      writeStored('declined');
      return;
    }

    // Small delay so it doesn't pop in before the page paints.
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, []);

  const accept = useCallback(() => {
    writeStored('accepted');
    setVisible(false);
  }, []);

  const decline = useCallback(() => {
    writeStored('declined');
    setVisible(false);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="dialog"
          aria-live="polite"
          aria-label="Cookie preferences"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
          className="fixed bottom-4 left-3 right-3 z-[80] max-w-[calc(100vw-1.5rem)] md:left-6 md:right-auto md:max-w-md"
          data-testid="cookie-consent"
        >
          <div className="overflow-hidden rounded-2xl bg-slate-900/95 p-5 text-slate-100 shadow-2xl ring-1 ring-white/10 backdrop-blur-md md:p-6">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-full bg-accent/15 text-accent flex items-center justify-center">
                <Cookie className="w-4.5 h-4.5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="font-serif text-base tracking-normal md:text-lg">
                    A quick note on cookies
                  </h2>
                  <button
                    type="button"
                    onClick={decline}
                    className="text-slate-400 hover:text-white transition -mr-1 -mt-1"
                    aria-label="Dismiss (decline non-essential cookies)"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  We use essential cookies to keep you logged in, plus optional
                  ones for anonymous analytics and crash reports. We never sell
                  your data or use ad trackers. Read more in our{' '}
                  <Link
                    to="/cookies"
                    className="text-accent font-medium hover:underline"
                  >
                    Cookie Policy
                  </Link>
                  .
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={accept}
                    className="inline-flex items-center justify-center rounded-full bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90 transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-accent"
                    data-testid="cookie-accept"
                  >
                    Accept all
                  </button>
                  <button
                    type="button"
                    onClick={decline}
                    className="inline-flex items-center justify-center rounded-full bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 ring-1 ring-white/15 hover:bg-white/10 transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-white/40"
                    data-testid="cookie-decline"
                  >
                    Essential only
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Helper for feature code — e.g. analytics bootstrap can check
 *   import { hasAnalyticsConsent } from '.../CookieConsent';
 *   if (hasAnalyticsConsent()) loadAnalytics();
 */
export function hasAnalyticsConsent() {
  const stored = readStored();
  return stored?.choice === 'accepted';
}
